import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { set as _set, get as _get, mergeWith as _mergeWith } from 'lodash';
import { makeInitialSettings } from '@/settings/itemSettings.defaults.js';

export const useItemSettingsStore = defineStore('itemSettings', () => {

    // Measures, Dimensions and Time Dimensions are collectively referred to as "Members"
    // Initialize the settings with a fresh copy of the defaults
    const settings = ref(makeInitialSettings());

    const getSeriesFormatting = computed(() => settings.value.chartFormatting.series)

    function setInitialSettings(payload){
        // Merge onto a *fresh* defaults object: merging onto a shared one let the
        // previously loaded chart's settings bleed into the next one.
        // Arrays are replaced wholesale rather than merged index-wise, so a
        // 2-series chart doesn't inherit series 3..6 from a 6-series chart.
        settings.value = _mergeWith(makeInitialSettings(), payload, replaceArrays);
    }

    function replaceArrays(objValue, srcValue){
        if (Array.isArray(srcValue)) {
            return srcValue
        }
    }

    // Object functions for settings
    function set(path, value = null){
        _set(settings.value, path, value)
    }

    function get(path, defaultValue = null){
        return _get(settings.value, path, defaultValue)
    }


    // Array functions for settings
    function upsert(path, valueObj){
        let settings = get(path)

        const index = settings.findIndex((element) => element.id === valueObj.id);
    
        // If the item does not exist, add it to the array
        // If it exists, update the existing item
        if (index === -1) {
            settings.push(valueObj);
        } else {
            // Check if the valueObj is an object, and update the existing item or create a new item based on the key of the valueObj
            if (typeof valueObj === 'object' && valueObj !== null) {
                Object.keys(valueObj).forEach((prop) => {
                    settings[index][prop] = valueObj[prop]; // Add new property
                })
            }
        }
    }

    function remove(id, path){
        let settings = get(path)

        let itemIndex = settings.findIndex((item) => item.id === id)
        
        settings.splice(itemIndex, 1)
    }

    /**
     * Adds all keys and respective values for a given array, into the settings object
     *
     * @param   {Array}  array     The array to iterate through (all objects that should be set)
     * @param   {String}  arrayKey  The key of the array to extract the property name from
     * @param   {String}  path      The main path of the property(ies) to set. Dot notation can be used.
     * @param   {Any}  value     The value to set for each property
     *
     * @return  {void}            
     */
    function setAll(array, arrayKey, path, value){

        if(Array.isArray(array) && array.length > 0){
            
            array.forEach(element => {
                let newPath = path + '.' + element[arrayKey]
                _set(settings.value, newPath, value)
            });
        }

    }

    function $reset() {
        settings.value = makeInitialSettings();
    }

    return {
        settings,
        getSeriesFormatting,
        setInitialSettings,
        set,
        get,
        upsert,
        remove,
        setAll,
        $reset
    }
});